import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const ServicesSection = () => {
  const [visibleCards, setVisibleCards] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const cardRefs = useRef([]);
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = translations[language].services;

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetch(`${API}/services`);
        if (response.ok) {
          const data = await response.json();
          if (data.length > 0) {
            setServices(data);
          } else {
            // Fallback to translated mock data if no services in DB
            setServices(t.list.map((item, idx) => ({
              id: idx + 1,
              number: String(idx + 1).padStart(2, '0'),
              title: item.title,
              description: item.description,
              image: getDefaultImage(idx)
            })));
          }
        }
      } catch (error) {
        console.error('Error fetching services:', error);
        // Fallback to translated mock data
        setServices(t.list.map((item, idx) => ({
          id: idx + 1,
          number: String(idx + 1).padStart(2, '0'),
          title: item.title,
          description: item.description,
          image: getDefaultImage(idx)
        })));
      }
      setLoading(false);
    };

    fetchServices();
  }, [t.list]);

  const getDefaultImage = (index) => {
    const images = [
      'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=800&h=600&fit=crop',
      'https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=800&h=600&fit=crop',
      'https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?w=800&h=600&fit=crop',
      'https://images.unsplash.com/photo-1558655146-d09347e92766?w=800&h=600&fit=crop',
      'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=600&fit=crop',
      'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop'
    ];
    return images[index % images.length];
  };

  useEffect(() => {
    const observers = cardRefs.current.map((ref, index) => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setVisibleCards((prev) => [...new Set([...prev, index])]);
            }
          });
        },
        { threshold: 0.2 }
      );

      if (ref) observer.observe(ref);
      return observer;
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, [services]);

  if (loading) {
    return (
      <section id="services" className="py-20 bg-slate-950 relative overflow-hidden">
        <div className="container mx-auto px-4 text-center">
          <div className="animate-pulse">
            <div className="h-8 bg-slate-800 rounded w-48 mx-auto mb-4"></div>
            <div className="h-12 bg-slate-800 rounded w-64 mx-auto"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="services" className="py-20 bg-slate-950 relative overflow-hidden" data-testid="services-section">
      {/* Background Effect */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-600/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-indigo-600/30 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <p className="text-sm uppercase tracking-widest text-gray-400 mb-4">{t.subtitle}</p>
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-8">{t.title}</h2>
        </div>

        {/* Services Grid - Compact Layout */}
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          {services.map((service, index) => (
            <div
              key={service.id}
              ref={(el) => (cardRefs.current[index] = el)}
              className={`transform transition-all duration-700 ${
                visibleCards.includes(index)
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
              data-testid={`service-card-${index}`}
            >
              <Card className="group relative bg-slate-900/80 backdrop-blur-sm border-slate-800 hover:border-pink-500/50 transition-all duration-500 overflow-hidden h-full shadow-lg hover:shadow-pink-500/20">
                {/* Pink Glow Effect */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-pink-500/30 rounded-full blur-3xl"></div>
                  <div className="absolute bottom-0 left-0 w-32 h-32 bg-pink-500/20 rounded-full blur-3xl"></div>
                </div>

                {/* Pink Accent Line */}
                <div className="absolute top-0 left-0 w-1 h-0 bg-gradient-to-b from-pink-500 via-pink-400 to-transparent group-hover:h-full transition-all duration-700"></div>
                
                <CardContent className="p-0 relative">
                  {/* Image */}
                  <div className="relative overflow-hidden">
                    <div className="aspect-[16/9] relative">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/70 to-transparent"></div>
                      
                      {/* Pink Glow Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-br from-pink-500/0 via-pink-500/0 to-pink-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                      
                      {/* Service Number Badge */}
                      <div className="absolute top-4 right-4">
                        <div className="relative">
                          <div className="absolute inset-0 bg-pink-500 rounded-full blur-xl opacity-50 group-hover:opacity-100 transition-opacity duration-500"></div>
                          <div className="relative bg-slate-900/90 backdrop-blur-sm border border-pink-500/50 rounded-full w-16 h-16 flex items-center justify-center">
                            <span className="text-pink-400 text-xl font-bold">{service.number}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 relative">
                    <h3 className="text-xl font-bold text-white mb-3 group-hover:text-pink-400 transition-colors duration-300 flex items-center justify-between">
                      <span>{service.title}</span>
                      <ArrowRight 
                        className="text-pink-500 opacity-0 group-hover:opacity-100 transform translate-x-0 group-hover:translate-x-2 transition-all duration-300" 
                        size={20} 
                      />
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed line-clamp-3">
                      {service.description}
                    </p>
                    
                    {/* Pink Bottom Accent */}
                    <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-pink-500 to-transparent group-hover:w-full transition-all duration-700"></div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-20 animate-fade-in">
          <p className="text-xl md:text-2xl text-gray-300 font-semibold">
            {t.cta1}
          </p>
          <h3 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-white via-pink-200 to-pink-400 mt-4">
            {t.cta2}
          </h3>
          
          {/* Check Packages Button */}
          <div className="mt-10">
            <Button
              onClick={() => {
                navigate('/pricing');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              size="lg"
              className="group relative bg-gradient-to-r from-pink-500 to-blue-500 hover:from-pink-600 hover:to-blue-600 text-white px-10 py-7 text-lg font-semibold rounded-xl shadow-2xl hover:shadow-pink-500/50 transition-all duration-500 transform hover:scale-110 overflow-hidden"
              data-testid="check-packages-btn"
            >
              {/* Shimmer effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              
              <span className="relative z-10 flex items-center gap-3">
                <Sparkles className="animate-pulse" size={20} />
                {language === 'ro' ? 'Vezi Pachetele' : 'Check Packages'}
                <ArrowRight className="group-hover:translate-x-2 transition-transform duration-300" size={20} />
              </span>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
